import React, { useState } from "react";
import { TFunction } from "i18next";
import { addMonths, buildMonthDays, formatISO, isBetween, isSameDay, parseISODate } from "./dateUtils";

interface InlineRangePickerProps {
  valueStart: string;
  valueEnd: string;
  onChange: (start: string, end: string) => void;
  t: TFunction;
}

const InlineRangePicker: React.FC<InlineRangePickerProps> = ({ valueStart, valueEnd, onChange, t }) => {
  const initialMonth = valueStart ? parseISODate(valueStart) || new Date() : new Date();
  const [visibleMonth, setVisibleMonth] = useState(
    new Date(Date.UTC(initialMonth.getUTCFullYear(), initialMonth.getUTCMonth(), 1))
  );
  const startDate = parseISODate(valueStart);
  const endDate = parseISODate(valueEnd);
  const cells = buildMonthDays(visibleMonth);
  const monthLabel = visibleMonth.toLocaleString("default", { month: "long", year: "numeric" });

  const handleSelect = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      onChange(formatISO(date), "");
      return;
    }
    if (startDate && !endDate) {
      if (date.getTime() < startDate.getTime()) {
        onChange(formatISO(date), formatISO(startDate));
      } else {
        onChange(formatISO(startDate), formatISO(date));
      }
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200/70 bg-white/80 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/50">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setVisibleMonth((m) => addMonths(m, -1))}
          className="rounded-full border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          ‹
        </button>
        <p className="text-sm font-semibold text-gray-800 capitalize dark:text-gray-100">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
          className="rounded-full border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          ›
        </button>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-500 dark:text-gray-400">
        {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1 text-sm">
        {cells.map((cell, idx) => {
          const isStart = startDate && isSameDay(cell.date, startDate);
          const isEnd = endDate && isSameDay(cell.date, endDate);
          const inRange = isBetween(cell.date, startDate, endDate);
          const disabled = !cell.inMonth;
          return (
            <button
              key={`${cell.date.toISOString()}-${idx}`}
              type="button"
              disabled={disabled}
              onClick={() => handleSelect(cell.date)}
              className={`relative h-10 rounded-lg px-1 text-center ${
                disabled
                  ? "text-gray-300 dark:text-gray-700"
                  : inRange || isStart || isEnd
                  ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-100"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              {isStart || isEnd ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      isStart && isEnd
                        ? "bg-indigo-600 text-white"
                        : isStart
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-500 text-white"
                    }`}
                  >
                    {cell.date.getUTCDate()}
                  </span>
                </span>
              ) : (
                <span>{cell.date.getUTCDate()}</span>
              )}
              {inRange && !(isStart || isEnd) && (
                <span
                  className="absolute inset-y-2 left-0 right-0 rounded-md bg-indigo-100 opacity-50 dark:bg-indigo-800/50"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
        <div>
          <p className="font-semibold">{t("classrooms.details.trip_request_start", { defaultValue: "Startdatum" })}</p>
          <p className="text-sm text-gray-700 dark:text-gray-200">{startDate ? formatISO(startDate) : "—"}</p>
        </div>
        <div>
          <p className="font-semibold">{t("classrooms.details.trip_request_expires", { defaultValue: "Einddatum" })}</p>
          <p className="text-sm text-gray-700 dark:text-gray-200">{endDate ? formatISO(endDate) : "—"}</p>
        </div>
      </div>
    </div>
  );
};

export default InlineRangePicker;
